import React from "react";
import { Box, Typography, Paper, keyframes } from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import HighlightOff from "@mui/icons-material/HighlightOff";
import PlayerHeadshot from "../PlayerHeadshot";
import { colors } from "../../theme";
import {
  ARCADE_ACCENT,
  formatStatValue,
  isTrueFalseOption,
  optionAnswerValue,
  trueFalseCardPresentation,
} from "../../utils/triviaArcade";

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
`;

const STATUS_CHROME = {
  correct: { borderColor: "#4caf50", bgcolor: "rgba(76, 175, 80, 0.15)" },
  wrong: { borderColor: "#f44336", bgcolor: "rgba(244, 67, 54, 0.12)" },
  selected: { borderColor: ARCADE_ACCENT, bgcolor: "background.paper" },
  idle: { borderColor: "transparent", bgcolor: "background.paper" },
};

const POLARITY_IDLE_BG = {
  positive: "rgba(34, 197, 94, 0.10)",
  negative: "rgba(239, 68, 68, 0.10)",
};

const optionCardChrome = (status, polarity) => {
  const chrome = STATUS_CHROME[status] || STATUS_CHROME.idle;
  if (status === "idle" && POLARITY_IDLE_BG[polarity]) {
    return { ...chrome, bgcolor: POLARITY_IDLE_BG[polarity] };
  }
  return chrome;
};

const RevealedStat = ({ subcategory, value }) => {
  if (value == null) return null;
  return (
    <Typography variant="body2" color="primary.main" fontWeight={700}>
      {formatStatValue(subcategory, value)}
    </Typography>
  );
};

const TrueFalseHero = ({ option }) => {
  const { label, polarity } = trueFalseCardPresentation(option);
  const Icon = polarity === "positive" ? CheckCircleOutline : HighlightOff;
  const color = polarity === "positive" ? colors.positive : colors.negative;

  return (
    <>
      <Icon sx={{ fontSize: 40, color }} aria-hidden />
      <Typography
        variant="h4"
        fontWeight={800}
        textAlign="center"
        letterSpacing={1}
        sx={{ color }}
      >
        {label}
      </Typography>
    </>
  );
};

const PlayerOptionHero = ({ option, sport }) => (
  <>
    <PlayerHeadshot
      playerId={option.playerId}
      src={option.photoUrl}
      sport={sport}
      size={72}
      variant="rounded"
      showTooltip={false}
    />
    <Typography variant="body1" fontWeight={600} textAlign="center">
      {option.displayName || option.text}
    </Typography>
    {option.team && (
      <Typography variant="body2" color="text.secondary">
        {option.team}
      </Typography>
    )}
  </>
);

const TriviaPlayerOptionCard = ({
  option,
  sport = "mlb",
  status = "idle",
  disabled = false,
  revealStats = false,
  questionSubcategory,
  onSelect,
}) => {
  const isTrueFalse = isTrueFalseOption(option);
  const presentation = isTrueFalse ? trueFalseCardPresentation(option) : null;
  const chrome = optionCardChrome(status, presentation?.polarity);
  const isWrong = status === "wrong";

  const handleClick = () => {
    if (!disabled) onSelect(optionAnswerValue(option));
  };

  return (
    <Paper
      onClick={handleClick}
      elevation={status === "selected" ? 6 : 2}
      sx={{
        p: 2,
        cursor: disabled ? "default" : "pointer",
        border: 2,
        borderColor: chrome.borderColor,
        bgcolor: chrome.bgcolor,
        animation: isWrong && !disabled ? `${shake} 0.4s ease` : undefined,
        transition: "transform 0.15s ease, border-color 0.15s ease",
        "&:hover": disabled
          ? {}
          : { transform: "translateY(-2px)", borderColor: ARCADE_ACCENT },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        {isTrueFalse ? (
          <TrueFalseHero option={option} />
        ) : (
          <PlayerOptionHero option={option} sport={sport} />
        )}
        {revealStats && (
          <RevealedStat
            subcategory={questionSubcategory}
            value={option.statValue}
          />
        )}
      </Box>
    </Paper>
  );
};

export default TriviaPlayerOptionCard;
